/**
 * TRIL-680: what an empty feed should say, and what it should offer.
 *
 * The empty branch is shared between the ordinary feed and a topic-filtered one, and the copy was
 * written for the first: "We're learning about your interests", with a Refresh button. On a topic
 * feed both are wrong. The message blames the viewer's interests when the cause is knowable and
 * narrower, and Refresh re-queries with `cursor: null`, so it returns the same empty page forever
 * -- a control that cannot change its own outcome.
 *
 * Since TRIL-677 uncapped the page scan for topic requests, an empty topic result is trustworthy:
 * it means "you have none", not "we stopped looking at page five". So the copy can say so plainly.
 *
 * Pure and separate from the component so both branches are testable without mounting the feed.
 *
 * TRIL-680 fix wave: this used to carry its own byte-identical copy of the server's
 * formatTopicLabel (a comment admitted it mirrored it) -- a THIRD copy of the casing logic,
 * opening a cross-tier drift (fixing an acronym server-side would leave this heading saying
 * "Ai" while the sidebar said "AI"). Now imports the single shared implementation.
 */
import { formatTopicLabel } from '@trilirivos/story-text';
import { FEED_CONSTANTS } from '../../constants/feed.constants';

export interface FeedEmptyState {
  heading: string;
  body: string;
  /** `back-to-feed` leaves the topic filter; `refresh` re-runs the same query. */
  action: 'back-to-feed' | 'refresh';
}

export function topicEmptyState(
  topic: string | undefined | null,
): FeedEmptyState {
  const active = topic?.trim() ? topic.trim() : null;

  if (!active) {
    return {
      heading: FEED_CONSTANTS.NO_RECOMMENDATIONS,
      body: "We're learning about your interests. Check back soon for personalized content!",
      action: 'refresh',
    };
  }

  return {
    heading: `Nothing trending in ${formatTopicLabel(active)}`,
    body: 'No stories in this topic have reached your feed yet.',
    action: 'back-to-feed',
  };
}
